import { Injectable, EventEmitter, Optional, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';

export interface MessageOptions {
  type?: string,
  icon?: string,
  xRayId?: string,
  errorInfo?: string,
}

@Injectable()
export class MessageService implements OnDestroy {
  routerSubscription: Subscription;

  showEvent = new EventEmitter();
  hideEvent = new EventEmitter();

  DEFAULTS = {
    error: {
      title: 'Oops, looks like something went wrong',
      type: 'error',
      icon: 'warning sign',
    },

    success: {
      title: 'Success',
      type: 'success',
      icon: 'check circle',
    },

    warning: {
      title: 'Warning',
      type: 'warning',
      icon: 'warning sign',
    },

    info: {
      title: 'Info',
      type: 'info',
      icon: 'info circle',
    },
  };

  constructor(@Optional() private router: Router) {
    if (router) {
      this.routerSubscription = router.events.subscribe(e => {
        if (e instanceof NavigationEnd) {
          this.hide();
        }
      });
    }
  }

  show(title: string, message: string, options: MessageOptions = {}) {
    const customOptions = {
      type: options.type || this.DEFAULTS.error.type,
      icon: options.icon || this.DEFAULTS.error.icon,
      xRayId: options.xRayId,
      errorInfo: options.errorInfo,
    };

    this.showEvent.next({ title, message, options: customOptions });
  }

  info(message: string) {
    const info = this.DEFAULTS.info;

    this.show(info.title, message, { type: info.type, icon: info.icon });
  }

  success(message: string) {
    const success = this.DEFAULTS.success;

    this.show(success.title, message, { type: success.type, icon: success.icon });
  }

  warning(message: string) {
    const warning = this.DEFAULTS.warning;

    this.show(warning.title, message, { type: warning.type, icon: warning.icon });
  }

  error(message: string, options?: { xRayId?: string }) {
    const error = this.DEFAULTS.error;

    this.show(error.title, message, { type: error.type, icon: error.icon, xRayId: options?.xRayId });
  }

  unauthorized(options?: { xRayId?: string }) {
    const error = this.DEFAULTS.error;
    this.show('Session expired', 'Your session has expired. Please login again to continue', { type: error.type, icon: error.icon, xRayId: options?.xRayId });
  }

  forbidden(options?: { xRayId?: string, errorInfo?: string, customErrorMsg?: string }) {
    const error = this.DEFAULTS.error;
    let  errorMsg = 'You don\'t have the necessary permissions to view all the data in this dataset. If you believe you should have access, please contact your administrator with the following reference code for assistance:';

    if (options.customErrorMsg) {
      errorMsg = options.customErrorMsg;
    }

    this.show('Access denied', errorMsg, { type: error.type, icon: error.icon, xRayId: options?.xRayId, errorInfo: options?.errorInfo });
  }

  hide() {
    this.hideEvent.next(null);
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
